/**
 * The drawing geometry the two dashboard stage widgets share.
 *
 * `StageDonut` and `FunnelBars` sit side by side in a two-column grid and both
 * render at `w-full`, so the browser scales each by (panel width ÷ viewBox
 * width). They only grow together if that viewBox width is the SAME number —
 * and they only stay the same HEIGHT if the donut's box is in the funnel's
 * proportion at the funnel's real maximum.
 *
 * This module exists because that number was got wrong twice in one PR, in
 * opposite directions:
 *
 *   1. The donut carried its own square 220 box under a `max-w-[320px]` cap.
 *      On a wide screen it stayed a small ring in a half-empty panel beside a
 *      funnel that filled its own. Reported from a screenshot, 2026-09-01.
 *   2. The cap came off and the donut's height was pinned to the funnel's shape
 *      at EIGHT rungs — a configuration the same PR made unreachable, because
 *      `Source` is rung 1 and is now filtered out. So the donut became the
 *      taller widget in every case, by up to 2.03x on a mid-flight search, and
 *      the whitespace moved to the funnel panel instead of disappearing.
 *
 * Neither was caught by a test, because jsdom runs no layout engine: "these two
 * scale together" has no observable signal there. What CAN be pinned is that
 * both derive from one place and that the derivation still matches the ladder —
 * `chartLayout.test.ts` does exactly that, so a stage leaving the filter or a
 * row height changing fails a test instead of quietly rotting the constant.
 *
 * It lives in its own module rather than in either widget because a constant
 * one chart imports from its sibling answers "which of the two owns the shared
 * shape?" arbitrarily, and the next widget that needs it would have no obvious
 * place to look. Raised by two reviewers independently on PR #797 iter 3.
 */

import { LADDER_STAGES } from "../../../../components/stages/ladder";
import { isNotCandidateStage } from "./seriesDefaults";

/** Drawing width of both widgets. The scale factor is panel width ÷ this. */
export const CHART_VIEW_WIDTH = 420;

/** One funnel rung, and the gap under it. */
export const FUNNEL_ROW_HEIGHT = 34;
export const FUNNEL_ROW_GAP = 3;

/** The funnel's viewBox height for a given number of rungs. */
export function funnelViewHeight(rungs: number): number {
  return rungs * (FUNNEL_ROW_HEIGHT + FUNNEL_ROW_GAP) - FUNNEL_ROW_GAP;
}

/**
 * How many rungs the funnel can actually draw.
 *
 * DERIVED, NOT COUNTED BY HAND, and that is the whole point of this file. The
 * funnel carries ladder rungs only — anything off the ladder is reported in the
 * roles-and-holds section instead — and `candidateFunnelSteps` then drops the
 * ones whose holders are not candidates. `Source` is both a rung and excluded,
 * so the maximum is 7 and not the 8 that `LADDER_STAGES.length` suggests.
 */
export const MAX_DRAWN_FUNNEL_RUNGS = LADDER_STAGES.filter(
  (stage) => !isNotCandidateStage(stage)
).length;

/**
 * The donut's viewBox height: the funnel's, at the tallest it can ever be.
 *
 * At a full funnel the two panels are the same height; below that the donut is
 * the taller of the pair, which is the direction that leaves the whitespace on
 * the funnel side where a short funnel has visibly less to say.
 */
export const DONUT_VIEW_HEIGHT = funnelViewHeight(MAX_DRAWN_FUNNEL_RUNGS);

/**
 * Ring radii, as a fraction of the donut box's half-height.
 *
 * Kept in the proportion the original square 220x220 ring used (outer 100 of
 * half-height 110; inner 58 of outer 100) so the ring's weight is unchanged —
 * only its size now tracks the box instead of a fixed pixel count.
 */
export const DONUT_OUTER_RADIUS = Math.round((DONUT_VIEW_HEIGHT / 2) * (100 / 110));
export const DONUT_INNER_RADIUS = Math.round(DONUT_OUTER_RADIUS * (58 / 100));
